/* ─────────────────────────────  DataTableExport.jsx  ─────────────────────────────
   Componente de tabla de datos con paginacion, exportacion a Excel y graficas
   ───────────────────────────────────────────────────────────────────────────────── */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { suggestChartTransformation, executeSQLDirect } from '../../utils/strandsClient';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import './DataTableExport.css';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Iconos SVG inline
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const SortAscIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 14l5-5 5 5H7z"/>
  </svg>
);

const SortDescIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 10l5 5 5-5H7z"/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10"/>
    <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const TableIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <line x1="3" y1="9" x2="21" y2="9"/>
    <line x1="3" y1="15" x2="21" y2="15"/>
    <line x1="9" y1="3" x2="9" y2="21"/>
    <line x1="15" y1="3" x2="15" y2="21"/>
  </svg>
);

const ChartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const BarChartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="12" width="4" height="9"/>
    <rect x="10" y="6" width="4" height="15"/>
    <rect x="17" y="3" width="4" height="18"/>
  </svg>
);

const LineChartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="22 12 18 8 12 14 8 10 2 16"/>
  </svg>
);

const PieChartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/>
    <path d="M22 12A10 10 0 0 0 12 2v10z"/>
  </svg>
);

// Colores para gráficas
const CHART_COLORS = [
  'rgba(7, 60, 92, 0.8)',
  'rgba(16, 185, 129, 0.8)',
  'rgba(59, 130, 246, 0.8)',
  'rgba(245, 158, 11, 0.8)',
  'rgba(239, 68, 68, 0.8)',
  'rgba(139, 92, 246, 0.8)',
  'rgba(236, 72, 153, 0.8)',
  'rgba(20, 184, 166, 0.8)',
  'rgba(251, 146, 60, 0.8)',
  'rgba(99, 102, 241, 0.8)',
];

const CHART_BORDER_COLORS = CHART_COLORS.map(c => c.replace('0.8', '1'));

/**
 * DataTableExport - Tabla de datos con paginacion, exportacion Excel y graficas
 */
function DataTableExport({
  data = [],
  metadata = {},
  sql = '',
  explanation = '',
  isLoading = false,
  error = null,
  onReload,
  onClose,
  pageSize = 50,
  currentLimit = 1000,  // Límite actual de filas
  canExport = true,
  onSaveChartToReport,  // (chartData) => void  — si esta, muestra "Guardar en reporte" en la grafica
  originalQuery = '',   // Pregunta original del usuario (para "Sugerir gráfica con IA")
  username = '',        // Username para llamadas al backend (contexto AWS)
  initialAiSuggestion = null,  // Si viene, hidratar aiSuggestedData al montar (persistencia)
  onAiSuggestionChange = null  // (suggestion|null) => void — el padre persiste la sugerencia
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Si el mensaje viene con una sugerencia IA persistida pero sin tabla
  // original (data principal vacía), abrir directo en la tab Gráfica para
  // que el usuario vea de inmediato la visualización que se guardó.
  const _hasOnlyAiSuggestion = !!initialAiSuggestion && (!data || data.length === 0);
  const [activeView, setActiveView] = useState(_hasOnlyAiSuggestion ? 'chart' : 'table');
  // Lazy init desde initialAiSuggestion para que cuando el componente se
  // monte con una sugerencia persistida, los selects del chart ya estén
  // sincronizados (sin esto, label/value quedaban vacíos y la gráfica no
  // se renderizaba aunque el badge "Vista generada por IA" sí apareciera).
  const [chartType, setChartType] = useState(initialAiSuggestion?.chartConfig?.chartType || 'bar');
  const [labelColumn, setLabelColumn] = useState(initialAiSuggestion?.chartConfig?.labelColumn || '');
  const [valueColumn, setValueColumn] = useState(initialAiSuggestion?.chartConfig?.valueColumn || '');
  const [selectedLimit, setSelectedLimit] = useState(currentLimit);
  const [capturingChart, setCapturingChart] = useState(false);
  // Sugerencia IA de gráfica: cuando la tabla no es directamente graficable
  // (sin numéricas, demasiadas filas únicas, etc.), pedimos a Claude que
  // proponga una transformación SQL agregada y la ejecutamos para graficar.
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestError, setAiSuggestError] = useState(null);
  // Inicializa desde `initialAiSuggestion` para persistir entre cerrar y
  // abrir el modal. El padre (chatbotui) la guarda en vizData[id_message].
  const [aiSuggestedData, setAiSuggestedData] = useState(initialAiSuggestion);
  const chartContainerRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Guardar en reporte contextual: si estás en tab Gráfica, captura el
  // canvas; si estás en tab Tabla, captura el div de la tabla con
  // html2canvas. Ambos casos terminan llamando a onSaveChartToReport
  // con un image_data_url, así el padre no necesita lógica distinta.
  const handleSaveCurrent = useCallback(async () => {
    if (!onSaveChartToReport) return;
    try {
      setCapturingChart(true);
      let dataURL;

      if (activeView === 'chart' && chartContainerRef.current) {
        // Chart.js renderiza la grafica en un <canvas> → captura directa
        const canvas = chartContainerRef.current.querySelector('canvas');
        if (canvas) {
          dataURL = canvas.toDataURL('image/png');
        } else {
          const captured = await html2canvas(chartContainerRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
            logging: false
          });
          dataURL = captured.toDataURL('image/png');
        }
      } else if (activeView === 'table' && tableContainerRef.current) {
        const captured = await html2canvas(tableContainerRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false
        });
        dataURL = captured.toDataURL('image/png');
      }

      if (!dataURL) {
        console.warn('[DataTableExport] No se pudo capturar el contenido');
        return;
      }

      onSaveChartToReport({
        type: activeView === 'chart' ? 'chart' : 'table',
        image_data_url: dataURL,
        chartType: activeView === 'chart' ? chartType : 'table',
        labelColumn: activeView === 'chart' ? labelColumn : '',
        valueColumn: activeView === 'chart' ? valueColumn : ''
      });
    } catch (err) {
      console.error('[DataTableExport] Error capturing:', err);
    } finally {
      setCapturingChart(false);
    }
  }, [onSaveChartToReport, activeView, chartType, labelColumn, valueColumn]);

  // Pide a Claude una sugerencia para graficar. Dos modos:
  //  - Con sql: genera SQL agregado nuevo → ejecuta → más datos para chart
  //  - Sin sql (tabla ya renderizada): solo pide chartConfig sobre los datos
  //    actuales → no ejecuta SQL, aplica config directamente
  const handleSuggestChart = useCallback(async () => {
    if (aiSuggesting) return;
    setAiSuggesting(true);
    setAiSuggestError(null);
    try {
      const cols = metadata.columns || [];
      const colTypes = metadata.column_types || {};
      const hasSql = !!sql;

      const suggestion = await suggestChartTransformation({
        originalQuery,
        currentSql: hasSql ? sql : '',
        sampleRows: hasSql ? undefined : data.slice(0, 10),
        columns: cols,
        columnTypes: colTypes,
        username,
      });
      if (!suggestion?.success || !suggestion.chartConfig) {
        throw new Error(suggestion?.error || 'No se pudo generar sugerencia');
      }

      // Auto-corrige el chartConfig contra las columnas reales: Claude a
      // veces propone label/value que no existen, o las dos iguales. Esto
      // genera gráficas vacías ("Sin valor" como única etiqueta). Si pasa,
      // elegimos la primera string como label y la primera numérica como
      // value de las columnas REALMENTE devueltas.
      const reconcileChartConfig = (cfg, realCols, realTypes) => {
        const numericTypes = ['integer','bigint','smallint','tinyint','double','float','real','decimal','number'];
        const isNumeric = (col) => {
          const t = (realTypes[col] || '').toLowerCase();
          return numericTypes.some(n => t.includes(n));
        };
        let label = cfg?.labelColumn;
        let value = cfg?.valueColumn;
        const labelOk = label && realCols.includes(label);
        const valueOk = value && realCols.includes(value);
        const distinct = label !== value;
        if (labelOk && valueOk && distinct) return cfg;

        // Fallback: primer string como label, primer numeric distinto como value
        const stringCol = realCols.find(c => !isNumeric(c));
        const numericCol = realCols.find(c => isNumeric(c) && c !== stringCol);
        const fallback = { ...cfg };
        if (!labelOk || !distinct) fallback.labelColumn = stringCol || realCols[0];
        if (!valueOk || !distinct) fallback.valueColumn = numericCol || realCols[realCols.length - 1];
        console.warn('[DataTableExport] chartConfig auto-corregido:', { antes: cfg, despues: fallback, realCols });
        return fallback;
      };

      let finalCfg;
      let nextSuggestion;
      if (hasSql && suggestion.sql) {
        // Modo SQL: ejecutar la transformación sugerida y usar sus resultados.
        const execResult = await executeSQLDirect(suggestion.sql, 100, username);
        if (!execResult?.success) {
          throw new Error(execResult?.error || 'Error ejecutando SQL sugerido');
        }
        const realCols = execResult.metadata?.columns || [];
        const realTypes = execResult.metadata?.column_types || {};
        finalCfg = reconcileChartConfig(suggestion.chartConfig, realCols, realTypes);
        nextSuggestion = {
          data: execResult.data || [],
          columns: realCols,
          column_types: realTypes,
          sql: suggestion.sql,
          chartConfig: finalCfg,
        };
      } else {
        // Modo Sample: aplicar chartConfig sobre los datos existentes.
        finalCfg = reconcileChartConfig(suggestion.chartConfig, cols, colTypes);
        nextSuggestion = {
          data: data,
          columns: cols,
          column_types: colTypes,
          sql: '',
          chartConfig: finalCfg,
        };
      }
      setAiSuggestedData(nextSuggestion);
      // Avisamos al padre para que persista la sugerencia y la siguiente
      // vez que se abra este modal con el mismo mensaje, ya esté hidratada.
      if (typeof onAiSuggestionChange === 'function') {
        onAiSuggestionChange(nextSuggestion);
      }

      // Sincronizar UI: aplicar selección y tipo sugeridos (ya conciliados)
      if (finalCfg.chartType) setChartType(finalCfg.chartType);
      if (finalCfg.labelColumn) setLabelColumn(finalCfg.labelColumn);
      if (finalCfg.valueColumn) setValueColumn(finalCfg.valueColumn);
    } catch (e) {
      console.error('[DataTableExport] Suggest chart error:', e);
      setAiSuggestError(e.message || 'Error generando sugerencia');
    } finally {
      setAiSuggesting(false);
    }
  }, [aiSuggesting, metadata, originalQuery, sql, username, data, onAiSuggestionChange]);

  // Limpia la sugerencia IA cuando el usuario cambia los datos base.
  const clearAiSuggestion = useCallback(() => {
    setAiSuggestedData(null);
    setAiSuggestError(null);
  }, []);

  // Opciones de límite disponibles
  const LIMIT_OPTIONS = [
    { value: 100, label: '100 filas' },
    { value: 500, label: '500 filas' },
    { value: 1000, label: '1,000 filas' },
    { value: 5000, label: '5,000 filas' },
    { value: 10000, label: '10,000 filas' }
  ];

  const columns = metadata.columns || [];
  const columnTypes = metadata.column_types || {};
  const totalRows = data.length;
  const totalPages = Math.ceil(totalRows / pageSize);

  // Determinar si columna es numerica. Primero mira el tipo declarado; si
  // no hay (caso típico de tablas markdown del bot que no traen schema),
  // inspecciona los datos: si la mayoría de las celdas parsea como número
  // finito, la trata como numérica. Cuando hay una sugerencia IA con
  // dataset propio, usa ese dataset y sus tipos para la inferencia.
  const isNumericColumn = useCallback((colName) => {
    const activeTypes = aiSuggestedData?.column_types || columnTypes;
    const activeData = aiSuggestedData?.data?.length ? aiSuggestedData.data : data;
    const declaredType = (activeTypes[colName] || '').toLowerCase();
    if (declaredType) {
      return ['integer', 'bigint', 'smallint', 'tinyint', 'double', 'float', 'real', 'decimal', 'numeric', 'number'].includes(declaredType);
    }
    const sample = activeData.slice(0, 20);
    if (sample.length === 0) return false;
    let numeric = 0;
    let nonEmpty = 0;
    for (const row of sample) {
      const v = row?.[colName];
      if (v === null || v === undefined || v === '') continue;
      nonEmpty++;
      const num = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
      if (!Number.isNaN(num) && Number.isFinite(num)) numeric++;
    }
    return nonEmpty > 0 && (numeric / nonEmpty) >= 0.7;
  }, [columnTypes, data, aiSuggestedData]);

  // Columnas numericas y de texto para graficas
  const numericColumns = useMemo(() =>
    columns.filter(col => isNumericColumn(col)),
    [columns, isNumericColumn]
  );

  const textColumns = useMemo(() =>
    columns.filter(col => !isNumericColumn(col)),
    [columns, isNumericColumn]
  );

  // Auto-seleccionar columnas para grafica
  useMemo(() => {
    if (!labelColumn && textColumns.length > 0) {
      setLabelColumn(textColumns[0]);
    }
    if (!valueColumn && numericColumns.length > 0) {
      setValueColumn(numericColumns[0]);
    }
  }, [textColumns, numericColumns, labelColumn, valueColumn]);

  // Ordenar datos
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortConfig.direction === 'desc' ? -comparison : comparison;
    });
  }, [data, sortConfig]);

  // Datos de pagina actual
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  // Preparar datos para grafica. Si hay una sugerencia IA aceptada,
  // usamos su tabla agregada en vez de `data` original.
  const chartSourceData = aiSuggestedData?.data?.length ? aiSuggestedData.data : data;
  const chartData = useMemo(() => {
    if (!labelColumn || !valueColumn || !chartSourceData.length) return null;

    // Agrupar por etiqueta y sumar valores (max 15 items)
    const grouped = {};
    chartSourceData.forEach(row => {
      const label = String(row[labelColumn] || 'Sin valor');
      const value = parseFloat(row[valueColumn]) || 0;
      grouped[label] = (grouped[label] || 0) + value;
    });

    // Ordenar por valor y tomar top 15
    const sorted = Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15);

    const labels = sorted.map(([label]) =>
      label.length > 25 ? label.substring(0, 25) + '...' : label
    );
    const values = sorted.map(([, value]) => value);

    return {
      labels,
      datasets: [{
        label: valueColumn,
        data: values,
        backgroundColor: CHART_COLORS.slice(0, values.length),
        borderColor: CHART_BORDER_COLORS.slice(0, values.length),
        borderWidth: 1,
      }]
    };
  }, [chartSourceData, labelColumn, valueColumn]);

  // Opciones de grafica
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: chartType === 'pie' || chartType === 'doughnut',
        position: 'right',
        labels: {
          font: { size: 11 },
          boxWidth: 12,
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed.y ?? context.parsed;
            return `${context.label}: ${value.toLocaleString('es-MX')}`;
          }
        }
      }
    },
    scales: chartType === 'bar' || chartType === 'line' ? {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => value.toLocaleString('es-MX')
        }
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: { size: 10 }
        }
      }
    } : undefined
  }), [chartType]);

  // Handlers
  const handleSort = (columnKey) => {
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        return {
          key: columnKey,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  const formatValue = (value, colName) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      return isNumericColumn(colName)
        ? value.toLocaleString('es-MX', { maximumFractionDigits: 2 })
        : value;
    }
    return String(value);
  };

  const handleExportExcel = useCallback(() => {
    if (!data.length) return;

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    const filename = `datos_export_${timestamp}.xlsx`;

    XLSX.writeFile(workbook, filename);
  }, [data]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Handler para cambiar límite y recargar
  const handleLimitChange = (newLimit) => {
    setSelectedLimit(newLimit);
    if (onReload && newLimit !== currentLimit) {
      onReload(newLimit);
    }
  };

  // Renderizar componente de grafica
  const renderChart = () => {
    if (!chartData) {
      return (
        <div className="chart-placeholder">
          <ChartIcon />
          <p>Selecciona columnas para visualizar</p>
        </div>
      );
    }

    const ChartComponent = {
      bar: Bar,
      line: Line,
      pie: Pie,
      doughnut: Doughnut
    }[chartType] || Bar;

    return <ChartComponent data={chartData} options={chartOptions} />;
  };

  // Estados de carga/error
  if (isLoading) {
    return (
      <div className="data-table-export data-table-loading">
        <div className="loading-spinner"></div>
        <p>Cargando datos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="data-table-export data-table-error">
        <p className="error-message">{error}</p>
        {onReload && (
          <button className="btn-reload" onClick={onReload}>
            <RefreshIcon /> Reintentar
          </button>
        )}
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="data-table-export data-table-empty">
        <p>No hay datos para mostrar</p>
        {onReload && (
          <button className="btn-reload" onClick={onReload}>
            <RefreshIcon /> Cargar datos
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="data-table-export">
      {/* Header */}
      <div className="data-table-header">
        <div className="data-table-info">
          <span className="row-count">{totalRows.toLocaleString()} filas</span>
          {explanation && <span className="explanation" title={explanation}>{explanation}</span>}
        </div>
        <div className="data-table-actions">
          {/* Tabs de vista */}
          <div className="view-tabs">
            <button
              className={`tab-btn ${activeView === 'table' ? 'active' : ''}`}
              onClick={() => setActiveView('table')}
            >
              <TableIcon /> Tabla
            </button>
            <button
              className={`tab-btn ${activeView === 'chart' ? 'active' : ''}`}
              onClick={() => setActiveView('chart')}
            >
              <ChartIcon /> Gráfica
            </button>
          </div>

          {/* Selector de límite de filas */}
          <div className="limit-selector">
            <select
              value={selectedLimit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              className="limit-select"
              title="Cambiar límite de filas"
            >
              {LIMIT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {canExport && (
          <button className="btn-export" onClick={handleExportExcel} title="Descargar Excel">
            <DownloadIcon /> Excel
          </button>
          )}
          {onReload && (
            <button className="btn-icon" onClick={() => onReload(selectedLimit)} title="Recargar">
              <RefreshIcon />
            </button>
          )}
          {/* Botón contextual: guarda la tabla o la gráfica según la tab
              activa. Va en el header — el de la vista interna fue
              eliminado para evitar duplicados. */}
          {onSaveChartToReport && (
            <button
              className="btn-save-to-report"
              onClick={handleSaveCurrent}
              disabled={capturingChart}
              title={activeView === 'chart' ? 'Guardar gráfica en reporte' : 'Guardar tabla en reporte'}
            >
              {capturingChart ? '⏳' : '📌'} {activeView === 'chart' ? 'Guardar gráfica' : 'Guardar tabla'}
            </button>
          )}
          {onClose && (
            <button className="btn-icon btn-close" onClick={onClose} title="Cerrar">
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {/* Vista de Tabla */}
      {activeView === 'table' && (
        <>
          <div className="data-table-container" ref={tableContainerRef}>
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col}
                      className={`${isNumericColumn(col) ? 'numeric' : ''} ${sortConfig.key === col ? 'sorted' : ''}`}
                      onClick={() => handleSort(col)}
                    >
                      <div className="th-content">
                        <span className="col-name">{col}</span>
                        <span className="sort-icon">
                          {sortConfig.key === col ? (
                            sortConfig.direction === 'asc' ? <SortAscIcon /> : <SortDescIcon />
                          ) : null}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {columns.map((col) => (
                      <td
                        key={col}
                        className={isNumericColumn(col) ? 'numeric' : ''}
                      >
                        {formatValue(row[col], col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginacion */}
          {totalPages > 1 && (
            <div className="data-table-pagination">
              <span className="page-info">
                Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalRows)} de {totalRows}
              </span>
              <div className="page-controls">
                <button
                  className="btn-page"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeftIcon />
                </button>
                <span className="page-number">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  className="btn-page"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRightIcon />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Vista de Grafica */}
      {activeView === 'chart' && (
        <div className="chart-section">
          {/* Banner: si la tabla NO es graficable y no hay sugerencia IA aún,
              ofrecer que Claude proponga. Funciona en dos modos:
              - Con sql → IA genera nuevo SQL agregado y lo ejecuta
              - Sin sql → IA solo recomienda chartConfig sobre los datos
                ya renderizados (tablas markdown del bot, etc.). */}
          {(sql || data.length > 0) && numericColumns.length === 0 && !aiSuggestedData && (
            <div className="ai-suggest-banner">
              <div className="ai-suggest-text">
                <strong>🔮 Esta tabla no tiene columnas numéricas para graficar directamente.</strong>
                <span>
                  {sql
                    ? 'Deja que la IA agrupe los datos por la categoría más relevante para que tenga sentido visualmente.'
                    : 'Deja que la IA elija las mejores columnas y tipo de gráfica para los datos que ya están en la tabla.'}
                </span>
              </div>
              <button
                className="ai-suggest-btn"
                onClick={handleSuggestChart}
                disabled={aiSuggesting}
                title="Generar gráfica con IA"
              >
                {aiSuggesting ? '⏳ Generando...' : '🔮 Sugerir gráfica con IA'}
              </button>
            </div>
          )}

          {/* Estado de error de sugerencia IA */}
          {aiSuggestError && (
            <div className="ai-suggest-error">
              ⚠️ {aiSuggestError}
              <button className="ai-suggest-retry" onClick={handleSuggestChart}>Reintentar</button>
            </div>
          )}

          {/* Badge cuando los datos vienen de una sugerencia IA. Antes
              mostraba el SQL generado (ruidoso para el usuario final);
              ahora solo muestra el título de la sugerencia y un botón para
              regenerar con otra llamada a Claude. */}
          {aiSuggestedData && (
            <div className="ai-suggest-applied">
              <span>
                ✨ Vista generada por IA
                {aiSuggestedData.chartConfig?.title
                  ? <> · <strong>{aiSuggestedData.chartConfig.title}</strong></>
                  : null}
              </span>
              <button
                className="ai-suggest-clear"
                onClick={handleSuggestChart}
                disabled={aiSuggesting}
                title="Pide a la IA otra propuesta"
              >
                {aiSuggesting ? '⏳ Regenerando…' : '🔄 Regenerar'}
              </button>
            </div>
          )}

          <div className="chart-header">
            <div className="chart-title">
              <ChartIcon /> Visualización de datos
            </div>
            <div className="chart-controls">
              {/* Selector de columna de etiquetas — opciones desde fuente activa */}
              <select
                className="chart-select"
                value={labelColumn}
                onChange={(e) => setLabelColumn(e.target.value)}
              >
                <option value="" disabled>Etiqueta (X)</option>
                {(aiSuggestedData?.columns || columns).map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>

              {/* Selector de columna de valores: en modo IA mostramos
                  todas las columnas (el backend ya garantiza que la
                  recomendada es numérica como 'total'). En modo normal
                  filtramos por tipo declarado para no ofrecer texto. */}
              <select
                className="chart-select"
                value={valueColumn}
                onChange={(e) => setValueColumn(e.target.value)}
              >
                <option value="" disabled>Valor (Y)</option>
                {(aiSuggestedData?.columns || columns)
                  .filter(col => aiSuggestedData ? true : isNumericColumn(col))
                  .map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
              </select>

              {/* Tipo de grafica */}
              <div className="chart-type-btns">
                <button
                  className={`chart-type-btn ${chartType === 'bar' ? 'active' : ''}`}
                  onClick={() => setChartType('bar')}
                  title="Barras"
                >
                  <BarChartIcon />
                </button>
                <button
                  className={`chart-type-btn ${chartType === 'line' ? 'active' : ''}`}
                  onClick={() => setChartType('line')}
                  title="Líneas"
                >
                  <LineChartIcon />
                </button>
                <button
                  className={`chart-type-btn ${chartType === 'pie' ? 'active' : ''}`}
                  onClick={() => setChartType('pie')}
                  title="Pastel"
                >
                  <PieChartIcon />
                </button>
                <button
                  className={`chart-type-btn ${chartType === 'doughnut' ? 'active' : ''}`}
                  onClick={() => setChartType('doughnut')}
                  title="Dona"
                >
                  <PieChartIcon />
                </button>
              </div>

              {/* Botón "Guardar en reporte" eliminado de aquí: ahora vive
                  en el header principal (a la derecha del Excel) y es
                  contextual según la tab activa (tabla / gráfica). */}
            </div>
          </div>

          <div className="chart-container" ref={chartContainerRef}>
            {renderChart()}
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTableExport;
